'use client';
import SectionPage from "@/components/common/section-page";

import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, } from "@/components/ui/breadcrumb"
import CardTitle from "@/components/common/card-title";
import AwesomeCard from "@/components/common/awesome-card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { cn } from "@/lib/utils";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import * as z from 'zod'
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Send } from "lucide-react";


const schema = z.object({
    nom: z.string().min(1, "Le nom est obligatoire"),
    email: z.string().email("L'adresse email est invalide"),
    sujet: z.string().min(1, "Le sujet est obligatoire"),
    message: z.string().min(1, "Le message est obligatoire")
});

export default function Home() {

    const form = useForm({
        resolver: zodResolver(schema),
        defaultValues: {
            nom: "",
            email: "",
            sujet: "",
            message: ""
        }
    });

    const onSubmit = (data: any) => {
        console.log(data)
    }

    return (
        <div className={"flex flex-col gap-6 pb-6 bg-slate-100"}>
            <CardTitle title={"Guide de voyage"}
                backgroundImgSrc="/images/card-title/Banniere-Guide-de-Voyage.jpg"
            />

            {/* Breadcrumb */}
            <SectionPage sectionType="2xl">
                <div className={"flex justify-between items-center"}>
                    <Breadcrumb>
                        <BreadcrumbList>
                            <BreadcrumbItem>
                                <BreadcrumbLink href={"/"}>Guide de voyage</BreadcrumbLink>
                            </BreadcrumbItem>
                        </BreadcrumbList>
                    </Breadcrumb>

                </div>
            </SectionPage>

            {/* Business & Leisure  */}
            <SectionPage sectionType="2xl">
                <AwesomeCard className={"bg-white text-muted-foreground"}
                    title="Formalités"
                    titleClassName={"text-bleu-marine"}>
                    <div className="flex flex-col gap-4 text-normal">
                        <p>Voyager en Côte d’Ivoire – Guide complet des conditions d’entrée 2025</p>
                        <ul className="list-disc list-inside">
                            <li>Passeport : valable 6 mois minimum</li>
                            <li>Visa : obligatoire pour la majorité des visiteurs hors CEDEAO</li>
                            <ul className="list-disc list-inside ml-6">
                                <li>Système e-visa : <a href="https://evisa-ivoirien.oneci.ci" target="_blank" className="text-blue-600" rel="noreferrer">evisa-ivoirien.oneci.ci</a></li>
                                <li>Autres infos : <a href="https://am.afdb.org" target="_blank" className="text-blue-600" rel="noreferrer">am.afdb.org</a>, <a href="https://embassies.net" target="_blank" className="text-blue-600" rel="noreferrer">embassies.net</a></li>
                            </ul>
                            <li>Documents nécessaires : formulaire de demande, photo d’identité, copie passeport, réservation vol/hôtel, certificat vaccination fièvre jaune selon nationalité</li>
                            <li>Conseils pratiques : vérifier passeport, préparer documents en papier et version électronique, respecter délais de demande</li>
                            <li>Liens officiels : <a href="https://evisa-ivoirien.oneci.ci" target="_blank" className="text-blue-600" rel="noreferrer">evisa-ivoirien.oneci.ci</a>, <a href="https://am.afdb.org" target="_blank" className="text-blue-600" rel="noreferrer">am.afdb.org</a>, <a href="https://embassies.net" target="_blank" className="text-blue-600" rel="noreferrer">embassies.net</a>, <a href="https://italie.diplomatie.gouv.ci" target="_blank" className="text-blue-600" rel="noreferrer">italie.diplomatie.gouv.ci</a></li>
                        </ul>
                        <p className="text-sm">(Téléchargement – Guides de voyage)</p>
                        <ul className="list-disc list-inside pl-6">
                            <li>Français : <a href="/pdfs/GUIDE-DE-VOYAGE-CI.pdf" target="_blank" className="text-blue-600">Télécharger le guide</a></li>
                            <li>English : <a href="/pdfs/TRAVEL-GUIDE.pdf" target="_blank" className="text-blue-600">Download the guide</a></li>
                            <li>Español : <a href="/pdfs/GUIA-DE-VIAJE.pdf" target="_blank" className="text-blue-600">Descargar la guía</a></li>
                            <li>Deutsch : <a href="/pdfs/REISEHANDBUCH.pdf" target="_blank" className="text-blue-600">Leitfaden herunterladen</a></li>
                        </ul>
                    </div>

                </AwesomeCard>
            </SectionPage>

            {/* contactez nous  */}
            <SectionPage sectionType="2xl">
                <AwesomeCard className={"bg-contactUs bg-cover text-muted-foreground text-white"}
                    title={"Contactez-nous"}
                    overlayClassName={"bg-bleu-marine opacity-60"}
                    titleClassName={"text-white"}>
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className={"grid grid-cols-1 md:grid-cols-2 gap-4"}>
                            <FormField
                                control={form.control}
                                name="nom"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Nom</FormLabel>
                                        <FormControl>
                                            <Input placeholder="Nom" {...field} className={"bg-white text-black border border-bleu-marine"} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="email"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Email</FormLabel>
                                        <FormControl>
                                            <Input placeholder="Email" {...field} className={"bg-white text-black border border-bleu-marine"} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="sujet"
                                render={({ field }) => (
                                    <FormItem className={"md:col-span-2"}>
                                        <FormLabel>Sujet</FormLabel>
                                        <FormControl>
                                            <Input placeholder="Sujet" {...field} className={"bg-white text-black border border-bleu-marine"} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="message"
                                render={({ field }) => (
                                    <FormItem className={"md:col-span-2"}>
                                        <FormLabel>Message</FormLabel>
                                        <FormControl>
                                            <Textarea placeholder="Message" {...field} rows={6} className={"bg-white text-black border border-bleu-marine"} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <div className="md:col-span-2">
                                <Button type={"submit"} className={"px-16 uppercase"} variant={"rose"}> <Send /> Envoyer</Button>

                                <p className={"text-center"}>
                                    Siège : Cocody Ambassade, derrière la Banque Mondiale
                                    <br />
                                    <br />
                                    01 BP 8538 ABIDJAN 01
                                    <br />
                                    Tél : + 225 27 22 22 41 10
                                    <br />
                                    Email : cote.ivoire.tourisme@gmail.com
                                </p>
                            </div>
                        </form>
                    </Form>
                </AwesomeCard>
            </SectionPage>


        </div>
    );
}
